import Backbone from "backbone";
import storage from "./storage/storage";
import { dispatcher, DispatcherEvents } from "./dispatcher";
import { handleApi, sendToErrorTracker } from "./error";
import { loginRedirect } from "./redirectioner";
import Translator from "../utils/translator";
import AppView from "../app.view";
import Eula from "../ui/eula";
import { CancelablePromise } from "../utils/cancelablePromise";
import { getCurrentHostName } from "./uri";
import { DataActions } from "../store/data/actions";
import { isInvalidSessionException } from "./errorHandler/error.types";
import { TripActions } from "../store/trips/actions";
import { TripAnnotationActions } from "../store/tripAnnotations/actions";
import { GroupLinkedDeviceActions } from "../store/groupLinkedDevice/actions";
import { UserUpdater } from "./userUpdater";
import { HosLogActions, HistoricalDayActions } from "../store/hosLogs/actions";
import { DeviceLinkActions } from "../store/deviceLinks/actions";
import { DvirLogActions } from "../store/dvirLogs/actions";
import { getUnsyncedDvirLogIds, getLogsSurvivingClear } from "../store/dvirLogs/reducers";
import { isMediaForDvirLog, isMediaPendingDelivery } from "../model/Image";
import { IImages } from "../store/data/types";
import { IDVIRLog } from "../model/DVIRLog";
import { FeedVersionActions } from "../store/feedVersions/actions";
import { TimeZoneInfoActions } from "../store/timeZoneInfo/actions";
import { DailyOdometerActions } from "../store/dailyOdometer/actions";
import { MessageActions } from "../store/messages/actions";
import { HosAnnotationActions } from "../store/hosAnnotations/actions";
import { extend, hashToArray } from "./vanilla";
import { DriverChangesActions } from "../store/driverChanges/actions";
import { DeviceDefectListActions, TrailerDefectListActions } from "../store/defectLists/actions";
import { TranslationActions } from "../store/translation/actions";
import { RouteActions } from "../store/routes/actions";
import { TrailerAttachmentActions } from "../store/trailerAttachments/actions";
import { UserHosRuleSetActions } from "../store/userHosRuleSet/actions";
import { CacheActions } from "../store/cache/actions";
import * as logger from "./logger";
import { clear } from "./native/secureStorageApi";
import { ScheduleStatusActions } from "../store/scheduleStatus/actions";
import { TMultiCallParam } from "./api.interfaces";
import { ISystemSettings, SYSTEM_SETTINGS_FIELDS } from "../store/systemSettings/types";
import { DriverSafetyActions } from "../modules/driverSafety/store/actions";
import { InspectionActions } from "../modules/inspections/store/actions";
import { CoachingActions } from "../modules/coaching/store/actions";
import { HosDisplayPreferencesActions } from "../modules/hos/store/actions";
import { clearAllLogsFromDb } from "./indexedDbLogger";
import { HosRuleResultsActions } from "../store/hosRuleResults/actions";
import { SuspiciousLogsSentActions } from "../store/suspiciousLogsSent/actions";
import { ErrorActions } from "../store/error/actions";
import { uploadEventLogsFromIndexedDb, getUploadChunkSize } from "../sync/eventLogs";
import IndexedDbMigrationFromRedux from "./storage/indexedDbMigrationFromRedux";
import { IndexedDbStorage } from "./storage/indexedDbStorage";
import { unregisterPushTokenOnDriveLogout } from "../modules/core/auth/services/drivePushTokenService";
import { resetColdChainAlertTracker } from "../modules/coldChain/services/coldChainAlertTracker";

type TSessionErrorType = "MissingClearances" | "DeclinedEula" | "InvalidLogin";
export interface ISessionError {
    type: TSessionErrorType;
    message?: (string | string[])[];
}

export default class Session extends Backbone.Model {
    private readonly app: AppView;

    constructor (options) {
        super(options);
        this.app = options.app;
        this.listenTo(dispatcher, DispatcherEvents.UserRequestLogout, this.onRequestLogout.bind(this));
    }

    defaults () {
        return {
            name: "Session"
        };
    }


    clearStore(currentUser: IUser, removeCredentials = false, skipNeedsCredentials = false) {
        if (currentUser) {
            let currentUserId = currentUser.id;
            let keepUsers = (this.app.userService.getUsers()).filter(user => user.id !== currentUserId) as IUser[];
            this.logRetainedUnsyncedDvirs(currentUser);
            this.app.store.dispatch(DeviceDefectListActions.clearDeviceDefectLists({ userId: currentUserId }));
            this.app.store.dispatch(DailyOdometerActions.clearDailyOdometer({ userId: currentUserId }));
            this.app.store.dispatch(HosRuleResultsActions.clearHosRuleResults({ userId: currentUserId }));
            this.app.store.dispatch(MessageActions.clearMessages({ userId: currentUserId }));
            this.app.store.dispatch(DriverChangesActions.clearDriverChanges(currentUserId));
            this.app.store.dispatch(RouteActions.clearRoutes({ userId: currentUserId }));
            this.app.store.dispatch(TrailerAttachmentActions.clearTrailerAttachments(currentUserId));
            this.app.store.dispatch(DeviceLinkActions.clearDeviceLinks(currentUserId));
            this.app.store.dispatch(DataActions.clearInspectionPin());
            this.app.store.dispatch(DataActions.clearAlertAvailability());
            this.app.store.dispatch(TrailerDefectListActions.clearTrailerDefectLists({ userId: currentUserId }));
            this.app.store.dispatch(TripActions.clearTrips(currentUserId));
            this.app.store.dispatch(TripAnnotationActions.clearTripAnnotations());
            this.app.store.dispatch(GroupLinkedDeviceActions.clearGroupLinkedDevices());

            this.app.store.dispatch(UserHosRuleSetActions.clearUserHosRuleSets(currentUserId));
            this.app.store.dispatch(HosAnnotationActions.clearAnnotations(currentUserId));
            this.app.store.dispatch(TimeZoneInfoActions.clearTimeZoneInfo({ userId: currentUserId }));
            this.app.store.dispatch(CacheActions.clearAllCachesForUser(currentUserId));

            this.app.store.dispatch(DataActions.clearDtcNextRun(currentUserId));
            this.app.store.dispatch(DataActions.clearInputHints(currentUserId));
            this.app.store.dispatch(DataActions.clearBoundaryLogDate(currentUserId));
            this.app.store.dispatch(DataActions.clearWorkday(currentUserId));
            this.app.store.dispatch(DataActions.clearTempAssets(currentUserId));
            this.app.store.dispatch(DataActions.clearAcl(currentUserId));
            if (keepUsers.length === 0) {
                this.app.store.dispatch(DataActions.clearGroupSecurityId());
            }
            const mediaToRetain = this.getMediaToRetain(keepUsers);
            this.logRetainedPendingMedia(currentUser, mediaToRetain);
            this.app.store.dispatch(DataActions.setImages(mediaToRetain));
            this.app.store.dispatch(SuspiciousLogsSentActions.clearSuspiciousLogsSent());
            this.app.store.dispatch(ErrorActions.clearOfflineError());

            this.app.store.dispatch(HosLogActions.clearUserHosLogs(keepUsers));
            this.app.store.dispatch(HosLogActions.clearSystemHosLogs(keepUsers));
            this.app.store.dispatch(HistoricalDayActions.clearTrackedDays(keepUsers));
            this.app.store.dispatch(DvirLogActions.clearDvirLogs(keepUsers));
            this.app.store.dispatch(FeedVersionActions.clearFeedVersions(keepUsers));
            this.app.store.dispatch(ScheduleStatusActions.clearScheduleStatus(keepUsers));
            this.app.store.dispatch(TranslationActions.clearTranslations({}));

            this.app.store.dispatch(DataActions.clearlastLoginDate(currentUser.name));
            this.app.manager.unregisterByUsername(currentUser.name, removeCredentials, skipNeedsCredentials);
            this.app.userService.removeUser(currentUser.name);

            // ###############################################################################################
            // ||                                                                                           ||
            // || Data "Clear" actions for Geotab Drive Modules stores.                                     ||
            // ||                                                                                           ||
            // || If you want your data stores to be cleared between user sessions (login/logout), you must ||
            // || make calls to your clear actions here.                                                    ||
            // ||                                                                                           ||
            // ###############################################################################################
            this.app.store.dispatch(DriverSafetyActions.clearDriverSafetyInsight(currentUser.id));
            this.app.store.dispatch(InspectionActions.clearInspectionState());
            this.app.store.dispatch(CoachingActions.clearCoachingState(currentUser.id));
            this.app.store.dispatch(HosDisplayPreferencesActions.clearSplitSleeperPreference(currentUser.id));
            // Not Redux-backed (module-level singleton, spec A6) — re-arms the silent breach
            // baseline for the next driver session rather than carrying over stale state.
            // Scoped to this user's id only, so a co-driver's concurrently active session
            // (another Context in the same Drive instance) is not affected by this logout.
            resetColdChainAlertTracker(currentUserId);
        }
    }

    async close(navigateCallback: (() => void) | null, noAudit: boolean, errorMessage?: string): Promise<void> {
        // Unregister push token BEFORE the API session is destroyed.
        // Must happen first because unregistration requires an authenticated API call.
        await unregisterPushTokenOnDriveLogout(this.app, this.app.store);

        let currentUser = this.app.userService.getCurrentUser() as INamedEntity;
        let destroyAndRedirectImpl = async () => {
            if (this.app.store.getState().activeUsers.length === 1) {
                // Get rid of local storage & logout from the API library
                if (IndexedDbMigrationFromRedux.isIndexedDbInUse) {
                    await IndexedDbStorage.clear();
                    await IndexedDbStorage.clear(undefined, "ADDIN");
                } else {
                    storage.clear();
                    storage.clear(undefined, "ADDIN");
                }
                // The storage clear above is the last step that can reject. The driver is leaving from here, so tell the
                // add-ins and drop their registrations; the producer's own driverLogout on removal would reach nobody.
                this.app.context?.addinService.releaseAllAddinEvents();
            }

            this.app.api.forget(undefined, errorMessage);
            this.clearStore(currentUser);
            void clear();

            logger.event(
                logger.Event.AUTHENTICATION,
                `Successfully logged out user ${ currentUser.name }. Current # of active users: ${ this.app.store.getState().activeUsers.length }`,
                currentUser
            );
            navigateCallback && navigateCallback();
        };
        const chunkSize = getUploadChunkSize();
        let shouldClearLogs = true;
        try {
            // The previous upload in workflow.ts with `-1` for `maxUploadLoops` will have attempted to upload all logs already,
            // but we'll call this one last time with a finite loop count to ensure we don't get stuck in an infinite loop in case of unforeseen issues
            const uploadSucceeded = await uploadEventLogsFromIndexedDb(this.app.api, chunkSize);
            if (!uploadSucceeded) {
                shouldClearLogs = false;
                logger.error(logger.Source.SYNC, "Logout: preserving event logs in IndexedDB — upload did not complete");
            }
        } catch (error) {
            sendToErrorTracker(error, "javascript");
        }
        this.auditAndRedirect(noAudit, currentUser, destroyAndRedirectImpl);
        if (shouldClearLogs) {
            await clearAllLogsFromDb();
        }
        return;
    }

    auditAndRedirect(noAudit: boolean, currentUser: INamedEntity, destroyAndRedirectImpl: () => void) {
        if (noAudit || !currentUser) {
            destroyAndRedirectImpl();
        } else {
            let api = this.app.api;
            let currentCredentials = api.getCurrentCredentials();
            this.app.context && this.app.context.addinService.destroy(); // we need to invalidate the sessionId create for add-in, if there is one, before we invalidate the user session
            let calls: TMultiCallParam[] = [
                ["Add", { typeName: "Audit", entity: { name: "UserLogoff", comment: currentUser.name } }]
            ];
            if (currentCredentials?.credentials.sessionId) {
                calls.push(["Logoff", { credentials: currentCredentials?.credentials }]);
            }
            api.multiCall(calls, destroyAndRedirectImpl, destroyAndRedirectImpl);
        }
    }

    start(translator: Translator, isCodriver: boolean, callback?: (error?: ISessionError) => void): void {
        this.refreshUser((user, errorMessage) => {
            if (!user) {
                // User wasn't in the DB
                this.close(null, true, errorMessage);
                return;
            }

            let vendor = geotab.location.getVendorPrefix();
            let removeUserFromLocalStorage = () => {
                this.app.manager.unregisterByUsername(user.name);
                this.app.store.dispatch(DataActions.clearAcl(user.id));
                this.app.api.forget();
            };
            let needsClearances = {
                type: "MissingClearances",
                message: this.app.securityService.getMissingClearances("useDrive", user.id)
            } as ISessionError;
            let canUseDrive = !needsClearances.message?.length;
            let isDriverChange = this.app.userService.getUsers().length > 1;

            if (!canUseDrive) {
                if (isDriverChange) {
                    this.updateSecurityClearances();
                    removeUserFromLocalStorage();
                } else {
                    removeUserFromLocalStorage();
                }

                callback && callback(needsClearances);
                return;
            }

            // MYG-33102: Log if missing KeyInformation clearance
            const missingClearancesForNoExceptionReport = this.app.securityService.getMissingClearances("useDriveWithoutExceptionsReport");
            if (missingClearancesForNoExceptionReport.length > 0) {
                logger.error(logger.Source.USER, `Missing clearances for 'useDriveWithoutExceptionsReport': ${ missingClearancesForNoExceptionReport.join(", ") }`, user);
            }

            // Make sure SystemSettings and FeatureFlags are downloaded early
            // after new session (i.e don't wait for scheduled tasks)
            this.retrieveEulaAndSystemSettings(vendor, isCodriver)
            .then(([paths, systemSettings]: [string[], any]) => {
                return Eula(this.app.store, translator, this.app.userService, systemSettings.jurisdiction || "", this.app.api, paths).show(user);
            })
            .then((reply: boolean) => {
                if (reply) {
                    callback && callback();
                } else {
                    removeUserFromLocalStorage();
                    callback && callback({ type: "DeclinedEula" });
                }
            })
            .catch(errorObj => {
                handleApi(errorObj);
                callback && callback();
            });

        });
    }

    retrieveEulaAndSystemSettings(vendor: IVendorPrefix, isCodriver: boolean) {
        if (isCodriver) {
            return CancelablePromise.all([
                this.app.api.callPromise("GetEULA", { domain: vendor.domain || "" }),
                CancelablePromise.resolve(this.app.store.getState().systemSettings)
            ]);
        }

        // Run GetFeatureFlagAuthenticated in parallel with the GetEULA + Get SystemSettings multiCall.
        // Both are independent HTTP requests with no data dependency between them.
        // getSystemSettings() reads feature flags from the store, so it must not execute until
        // downloadFeatureFlags has resolved and populated the store — both are awaited first.
        return Promise.all([
            this.app.featureFlagService.downloadFeatureFlags(this.app.api),
            this.app.api.multiCallPromise([
                ["GetEULA", { domain: vendor.domain || "" }],
                ["Get", { typeName: "SystemSettings", propertySelector: { fields: SYSTEM_SETTINGS_FIELDS, isIncluded: true } }]
            ])
        ]).then(([, [eulaResult, systemSettingsData]]: [void, [string[], ISystemSettings[]]]) => {
            return CancelablePromise.all([
                CancelablePromise.resolve(eulaResult),
                this.app.databaseCache.getSystemSettings(systemSettingsData)
            ]);
        });
    }

    refreshUser(callback?: (user?: INamedEntity, errorMessage?: string) => void) {
        let api = this.app.api;
        void api.getSession(null).then(({ credentials, server }) => {
            credentials.userName = credentials.userName.toLowerCase();
            // Don't navigate away if the server is 127.0.0.1 (that means we're authenticated against the current server)
            if (server && server !== getCurrentHostName() && server !== "127.0.0.1") {
                // We're authenticated against changed servers, cap'n! Set sail for unknown lands!
                logger.event(logger.Event.DEBUG_LOGINVIEW_NAVIGATE, `Session: Setting sail to ${ server } cap'n!`);
                loginRedirect(server, credentials);
                return;
            }

            new UserUpdater(api, this.app.userService, this.app.securityService, this.app.store)
                .updateUser(credentials.userName, callback, true)
                .catch(error => {
                    handleApi(error);
                    let errorMessage = "";
                    // This type of error has its own message on login.ts
                    if (!isInvalidSessionException(error)) {
                        errorMessage = "Please try again.";
                    }
                    callback && callback(this.app.userService.getCurrentUser() || undefined, errorMessage);
                });
        });
    }

    updateSecurityClearances() {
        let clearances: ISecurityGroup[] = extend(true, [], this.app.store.getState().data.groupSecurityId);
        let activeUsers = this.app.userService.getUsers(true) || [];
        let activeUser = activeUsers[0];

        if (activeUser) {
            let newAcl = this.app.securityService.calculatePermissions(clearances, activeUser.securityGroups);
            this.app.store.dispatch(DataActions.setAcl({
                [activeUser.id]: newAcl
            }));
        }
    }

    /**
     * The photos that must outlive this cleanup, keyed as `data.images` is.
     *
     * `data.images` is a single dictionary with no user key, so `setImages({})` was never scoped to
     * the driver being cleared — it also destroyed a still-signed-in co-driver's pending photos.
     * This rebuilds the dictionary instead, keeping an entry when either:
     *
     * - its bytes have not reached the server, whoever captured it. This covers the retained
     *   inspection's photos and a photo captured for an inspection still in progress, which has no
     *   DVIR log to be matched against yet; or
     * - it is on the server but not yet attached to a defect remark, and the inspection it belongs
     *   to is one this cleanup retains.
     *
     * Everything else is disposable cache and is dropped exactly as before — photos of inspections
     * already on the server are freed, so cleanup does not accumulate data it previously released.
     *
     * Only the second branch is bounded, by an inspection that is itself retained. The first is
     * not: a photo whose upload can never succeed — uploadMediaFiles cannot read a file the OS has
     * evicted — stays undelivered forever and is kept on every pass, with no equivalent of the
     * `_syncCounter` budget and terminal report the DVIR side has. Accepted deliberately, because
     * dropping an undelivered photo is the failure this ticket exists to fix; draining these is a
     * known follow-up rather than something this predicate handles.
     *
     * Surviving inspections come from getLogsSurvivingClear, the same rule the clearDvirLogs
     * reducer applies: the media kept has to match the logs kept.
     */
    private getMediaToRetain(keepUsers: IUser[]): IImages {
        const state = this.app.store.getState();
        const images = state.data.images;
        // Asked of the reducer's own rule rather than restated here, and read before clearDvirLogs
        // dispatches. Restating it would miss a driver an earlier cleanup removed from the user
        // service: they are neither the current user nor a kept user, yet their unsynced logs are
        // retained on every pass, and their media would be dropped out from under them.
        const survivingByUser = getLogsSurvivingClear(
            state.dvirLogs ?? {},
            keepUsers.map(user => user.id)
        );
        const survivingLogs: IDVIRLog[] = [];
        Object.keys(survivingByUser).forEach(userId => {
            survivingLogs.push(...hashToArray(survivingByUser[userId]));
        });

        return Object.keys(images).reduce((retained, mediaId) => {
            const image = images[mediaId];
            const keep = isMediaPendingDelivery(image)
                || (!image.isLinkedWithRemark && survivingLogs.some(log => isMediaForDvirLog(image, log)));
            if (keep) {
                // Shallow: every field is a scalar except deviceId, which nothing mutates in
                // place. The setImages reducer deep-clones the payload anyway, so a recursive copy
                // here would be the second of two.
                retained[mediaId] = { ...image } as IImages[string];
            }
            return retained;
        }, {} as IImages);
    }

    /**
     * Reports the photos kept by `getMediaToRetain`, for the same reason
     * {@link logRetainedUnsyncedDvirs} reports the inspections: a record that is retained but not
     * yet delivered must not be invisible. A retained inspection whose photo went missing would
     * otherwise look identical in the logs to one that never had a photo at all.
     */
    private logRetainedPendingMedia(currentUser: IUser, mediaToRetain: IImages) {
        const retainedIds = Object.keys(mediaToRetain);
        if (retainedIds.length > 0) {
            logger.log(
                logger.Source.SYNC,
                `Session cleared with inspection media not yet on the server. It is retained locally, eligible for this device's next media sync. Media IDs: ${ retainedIds.join(", ") }.`,
                currentUser
            );
        }
    }

    /**
     * Records any DVIR logs that will outlive this cleanup because they have never reached the
     * server. The reducer keeps them so completed driver work is not destroyed, but retention is
     * not delivery: login syncs exclude DVIR (syncsForLogin), so the record only becomes eligible
     * at the driver's next DVIR sync — and without a log line the waiting state is invisible,
     * which is what made ESUPSD-52472 impossible to confirm after the fact.
     */
    private logRetainedUnsyncedDvirs(currentUser: IUser) {
        const dvirLogs = this.app.store.getState().dvirLogs?.[currentUser.id];
        if (!dvirLogs) {
            return;
        }

        const retainedIds = getUnsyncedDvirLogIds(dvirLogs);
        if (retainedIds.length > 0) {
            // Logged at LOG rather than ERROR: retaining the record is the correct outcome. Both
            // levels reach TracedText, so the state stays visible without competing with real
            // errors during triage.
            logger.log(
                logger.Source.SYNC,
                `Session cleared for a driver with DVIR logs not yet on the server. They are retained locally, eligible for this driver's next DVIR sync. DVIR IDs: ${ retainedIds.join(", ") }.`,
                currentUser
            );
        }
    }

    private onRequestLogout(errorMessage: string) {
        this.close(null, true, errorMessage);
    }
}
